'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { useEffect } from 'react'

/**
 * --------------------------------------------------------------------------
 * Page data push
 * --------------------------------------------------------------------------
 **/
export function GTMPushPostMeta({ post = null }) {
  useEffect(() => {
    if (!post) {
      return
    }

    if (post) {
      const {
        type: postType,
        taxonomy: taxonomy,
        acf: {
          internal_data: {
            content_owner: jflContentOwner,
            priority_audience: jflContentAudience,
            objective: jflContentObjective,
            content_lifespan: jflContentLifespan,
          } = {},
        } = {},
      } = post

      if (jflContentOwner) {
        sendGTMEvent({ jfl_contentOwner: jflContentOwner })
      }

      if (jflContentAudience) {
        sendGTMEvent({ jfl_contentAudience: jflContentAudience })
      }

      if (jflContentObjective) {
        sendGTMEvent({ jfl_contentObjective: jflContentObjective })
      }

      if (jflContentLifespan) {
        sendGTMEvent({ jfl_contentLifespan: jflContentLifespan })
      }

      let contentType = null

      switch (postType) {
        case 'all-news':
          contentType = post['all-news-types']?.map((type) => type?.name)
          break
        case 'all-work':
          contentType = post['all-work-types']?.map((type) => type?.name)
          break
        case 'all-events':
          contentType = post['all-event-types']?.map((type) => type?.name)
          break
      }

      // News, Events and Work "Types" with fallback to post type/category name
      sendGTMEvent({ contentType: contentType ?? postType ?? taxonomy })

      // 'Company Name' or 'Jersey Finance'
      const contentAuthor = post?.acf?.business?.post_title ?? 'Jersey Finance'
      sendGTMEvent({ contentAuthor: contentAuthor })

      // Topics taxonomy
      const contentTopic = post['all-topics']?.map((topic) => topic?.name) ?? []
      sendGTMEvent({ contentTopic: contentTopic })
    }
  }, [post])

  return null
}

/**
 * --------------------------------------------------------------------------
 * User data push
 * --------------------------------------------------------------------------
 **/
export function GTMPushUserMeta({ user = null }) {
  useEffect(() => {
    if (!user) {
      sendGTMEvent({ memberLoggedIn: false })

      return
    }

    const {
      meta: {
        ID: memberId,
        user_email: memberEmail,
        job_title: memberJobTitle,
        market_interests: memberMarketInterests,
        market_pillars: memberMarketPillars,

        company_name: companyName,
        company_sectors: companySectors,
        company_subscription_type: companySubscriptionType,
      } = {},
    } = user

    sendGTMEvent({ memberLoggedIn: true })

    if (memberId) {
      sendGTMEvent({ memberId: memberId })
    }

    if (memberEmail && memberEmail.split('@')[1]) {
      sendGTMEvent({ memberEmail: memberEmail })
      sendGTMEvent({ memberEmailDomain: memberEmail.split('@')[1] })
    }

    if (memberJobTitle) {
      sendGTMEvent({ memberJobTitle: memberJobTitle })
    }

    if (memberMarketInterests) {
      sendGTMEvent({ memberMarketInterests: memberMarketInterests })
    }

    if (memberMarketPillars) {
      sendGTMEvent({ memberMarketPillars: memberMarketPillars })
    }

    if (companyName) {
      sendGTMEvent({ companyName: companyName })
    }

    if (companySectors) {
      sendGTMEvent({ companySectors: companySectors })
    }

    if (companySubscriptionType) {
      sendGTMEvent({ companySubscriptionType: companySubscriptionType })
    }
  }, [user])

  return null
}
